/**
 * Self-service contact-card API for Aegix One (ADR-141 §5).
 *
 * Typed `client.GET/POST/...` over `/api/v2/me/contact*` — the same
 * endpoints AIM's `main/services/contacts` wraps. Auth rides ONE's shared
 * http client (`lib/http-client.ts` → `oneStorage`), so there is no raw
 * token/storage access here (auth-lint: aegix/no-raw-token-storage-access).
 *
 * Feature gating: ONE has no FeatureCode enum / feature registry client.
 * The backend gates every me/contact* view with
 * `MassNotificationFeaturePermission` (MASS_NOTIFICATION_CONTACTS flag +
 * `mass_notification` Module via `has_feature(check_license=True)`), and
 * `_caller_district` also raises PermissionDenied for users without an
 * organization context — both surface as HTTP 403. `getMyContact` maps
 * 403 → `null` so the profile section can render nothing when the
 * capability isn't available to this user/org.
 */

import { client, unwrap } from '../lib/http-client';
import type { components } from '@tiaki/http-client/types';

export type MyContactCard = components['schemas']['Contact'];
export type MyContactDestination = components['schemas']['ContactDestination'];

/**
 * The caller's own contact card (lazy-created server-side).
 * Returns `null` when the org/user doesn't have the mass-notification
 * capability (HTTP 403) — callers treat that as "feature off".
 */
export async function getMyContact(): Promise<MyContactCard | null> {
  const result = await client.GET('/api/v2/me/contact/');
  if (result.response?.status === 403) return null;
  return unwrap(result) as MyContactCard;
}

/** Add a destination. New rows are UNVERIFIED until the §6 flow stamps them. */
export async function addMyContactDestination(
  channelCode: string,
  address: string,
): Promise<MyContactDestination> {
  const result = await client.POST('/api/v2/me/contact/destinations/', {
    body: { channel_code: channelCode, address },
  });
  return unwrap(result) as MyContactDestination;
}

/** Patch a destination (enable/disable, priority, address). */
export async function updateMyContactDestination(
  id: number,
  patch: { enabled?: boolean; priority?: number; address?: string },
): Promise<MyContactDestination> {
  const result = await client.PATCH(
    '/api/v2/me/contact/destinations/{destination_id}/',
    {
      params: { path: { destination_id: id } },
      body: patch,
    },
  );
  return unwrap(result) as MyContactDestination;
}

/** Remove a destination. */
export async function removeMyContactDestination(id: number): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/me/contact/destinations/{destination_id}/',
    {
      params: { path: { destination_id: id } },
    },
  );
  unwrap(result);
}

/**
 * Send the §6 verification message for a destination (mail: signed link;
 * phone: one-time SMS code; other kinds answer 409).
 */
export async function sendDestinationVerification(id: number): Promise<void> {
  const result = await client.POST(
    '/api/v2/me/contact/destinations/{destination_id}/send-verification/',
    {
      params: { path: { destination_id: id } },
    },
  );
  unwrap(result);
}
